// src/services/api.ts
import axios from 'axios';
import type { Stats, TopIp, DatabaseStatus, ExternalApiStatus, Settings, IpLookupResult } from '../types';

// API base URL - IpInfo.Api runs on port 5020
const API_BASE_URL = process.env.REACT_APP_API_URL ||  'http://localhost:5020';
const API_KEY = 'AdminKey_7k9mP2xR8wQ5nL3vH6jC4sT1yB0zF9gD';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Api-Key': API_KEY
  }
});

export const apiService = {
  // Admin Stats
  async getStats(): Promise<Stats> {
    const response = await api.get<Stats>('/api/admin/stats');
    return response.data;
  },

  async getTopIps(hours: number = 24): Promise<TopIp[]> {
    const response = await api.get<TopIp[]>(`/api/admin/top-ips?hours=${hours}`);
    return response.data;
  },

  async getDatabaseStatus(): Promise<DatabaseStatus> {
    const response = await api.get<DatabaseStatus>('/api/admin/database-status');
    return response.data;
  },

  async getExternalApiStatus(): Promise<ExternalApiStatus[]> {
    const response = await api.get<ExternalApiStatus[]>('/api/admin/external-api-status');
    return response.data;
  },

  async getSettings(): Promise<Settings> {
    const response = await api.get<Settings>('/api/admin/settings');
    return response.data;
  },

  async updateSettings(settings: Settings): Promise<void> {
    await api.post('/api/admin/settings', settings);
  },

  // Health Check
  async getDetailedHealth(): Promise<any> {
    const response = await api.get('/health/detailed');
    return response.data;
  },

  // IP Lookup endpoints
  async lookupIp(ipAddress: string, endpointType: 'location' | 'security' | 'network' | 'reputation' | 'full'): Promise<IpLookupResult> {
    const response = await api.get<IpLookupResult>(`/api/ip/${ipAddress}/${endpointType}`);
    return response.data;
  },

  async getMyIp(): Promise<{ ip: string }> {
    const response = await api.get<{ ip: string }>('/api/ip/myip');
    return response.data;
  },

  async batchLookup(ipAddresses: string[], endpointType: 'location' | 'security' | 'full'): Promise<any> {
    const response = await api.post(`/api/ip/batch/${endpointType}`, { ipAddresses });
    return response.data;
  },
};

// Mock service for development/testing (keep this for fallback)
export const mockService = {
  getStats(): Stats {
    return {
      totalRequests: 15234,
      cacheHitRate: 73.5,
      avgResponseTime: 42,
      activeConnections: 8,
      errorsToday: 3,
      torNodesCount: 1247,
    };
  },

  getTopIps(): TopIp[] {
    return [
      { ip: '8.8.8.8', country: 'US', count: 523 },
      { ip: '1.1.1.1', country: 'US', count: 412 },
      { ip: '104.200.20.46', country: 'US', count: 387 },
      { ip: '185.220.101.1', country: 'DE', count: 256 },
      { ip: '94.142.241.111', country: 'NL', count: 198 },
    ];
  },

  getDatabaseStatus(): DatabaseStatus {
    return {
      maxMindCity: 'GeoLite2-City.mmdb (Last updated: 2024-01-15)',
      maxMindAsn: 'GeoLite2-ASN.mmdb (Last updated: 2024-01-15)',
      torExitNodes: '1,247 nodes (Last update: 2024-01-20 08:30)',
      cacheEntries: 45632,
    };
  },

  getExternalApiStatus(): ExternalApiStatus[] {
    return [
      { name: 'IP-API.com', status: 'online', lastCheck: new Date(), responseTime: 156 },
      { name: 'TOR Project', status: 'online', lastCheck: new Date(), responseTime: 89 },
      { name: 'MaxMind GeoLite2', status: 'online', lastCheck: new Date(), responseTime: 5 },
    ];
  },

  getSettings(): Settings {
    return {
      cache: {
        locationExpiryDays: 30,
        securityExpiryDays: 7,
        networkExpiryDays: 30,
        reputationExpiryDays: 1,
      },
      torProject: {
        updateIntervalHours: 24,
        bulkListUrl: 'https://check.torproject.org/torbulkexitlist',
      },
      externalApis: {
        ipApiRateLimit: 45,
        ipApiTimeout: 5000,
      },
    };
  },

  async updateSettings(settings: Settings): Promise<void> {
    console.log('Mock: Settings updated', settings);
  },

  getDetailedHealth(): any {
    return {
      status: 'healthy',
      timestamp: new Date(),
      checks: [
        { name: 'Database', status: 'healthy', message: 'Connected successfully' },
        { name: 'MaxMind GeoLite2', status: 'healthy', message: 'Databases loaded' },
        { name: 'TOR Detection', status: 'healthy', message: '1,247 exit nodes loaded' },
        { name: 'External API', status: 'healthy', message: 'All APIs responding' },
        { name: 'Cache Performance', status: 'healthy', message: '73.5% hit rate' },
      ],
    };
  },

  async lookupIp(ipAddress: string, endpointType: string): Promise<IpLookupResult> {
    return {
      ip: ipAddress,
      type: endpointType,
      data: { message: 'Mock data - connect to real API' },
      metadata: {
        cached: false,
        timestamp: new Date().toISOString(),
        apiVersion: '1.0',
      },
    };
  },

  async getMyIp(): Promise<{ ip: string }> {
    return { ip: '203.0.113.1' };
  },
};

// Export the real API service by default
export default apiService;